/*
 *  Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
 *
 *  Licensed under the Apache License, Version 2.0 (the "License").
 *  You may not use this file except in compliance with the License.
 *  A copy of the License is located at
 *
 *  http://aws.amazon.com/apache2.0
 *
 *  or in the "license" file accompanying this file. This file is distributed
 *  on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 *  express or implied. See the License for the specific language governing
 *  permissions and limitations under the License.
 */

import _ from 'lodash';
import React from 'react';
import { decorate, action, observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import { withRouter } from 'react-router-dom';
import { Icon, Checkbox, Popup } from 'semantic-ui-react';

import jwtDecode from 'jwt-decode';
import { displayError } from '../helpers/notification';
import { branding } from '../helpers/settings';
import { createLink } from '../helpers/routing';
import { storage } from '../helpers/utils';
import localStorageKeys from '../models/constants/local-storage-keys';

const GUIDES_STORAGE_KEY = 'swb-dismissed-guides';
const WELCOME_STORAGE_KEY = 'swb-welcome-widget-dismissed';

// Top navigation bar — RMIT-style navy bar with red logo tab
// Includes responsive mobile menu (hamburger → fullscreen overlay)
// expected props
// - userStore (via injection)
// - authentication (via injection)
// - assets (via injection)
// - menuItems (via prop from MainLayout)
class TopNav extends React.Component {
  constructor(props) {
    super(props);
    runInAction(() => {
      this.mobileMenuOpen = false;
      this.userMenuOpen = false;
    });
  }

  handleLogout = async event => {
    event.preventDefault();
    event.stopPropagation();
    try {
      await this.props.authentication.logout();
    } catch (error) {
      displayError(error);
    }
  };

  handleRestoreGuides = () => {
    try {
      localStorage.removeItem(GUIDES_STORAGE_KEY);
      localStorage.removeItem(WELCOME_STORAGE_KEY);
    } catch (_) { /* noop */ }
    this.userMenuOpen = false;
    window.location.reload();
  };

  toggleUserMenu = () => {
    this.userMenuOpen = !this.userMenuOpen;
  };

  closeUserMenu = () => {
    this.userMenuOpen = false;
  };

  toggleMobileMenu = () => {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  };

  closeMobileMenu = () => {
    this.mobileMenuOpen = false;
  };

  gotoMobile = pathname => {
    this.closeMobileMenu();
    const location = this.props.location;
    const link = createLink({ location, pathname });
    this.props.history.push(link);
  };

  getSessionTimes() {
    try {
      const idToken = storage.getItem(localStorageKeys.appIdToken);
      if (!idToken) return null;
      const decoded = jwtDecode(idToken);
      const authTime = decoded.auth_time ? new Date(decoded.auth_time * 1000) : null;
      const expTime = decoded.exp ? new Date(decoded.exp * 1000) : null;
      if (!authTime || !expTime) return null;
      const fmt = date => date.toLocaleString();
      const hasRefreshToken = !!storage.getItem(localStorageKeys.appRefreshToken);
      return { start: fmt(authTime), expiry: fmt(expTime), hasRefreshToken };
    } catch (e) {
      return null;
    }
  }

  render() {
    const currentUser = this.props.userStore.user;
    const displayName = currentUser ? currentUser.displayName : 'Not Logged In';
    const isAdmin = currentUser ? currentUser.isAdmin : false;
    const researcherView = this.props.userStore.researcherView;

    return (
      <>
        <nav className="rmit-top-nav" id="main-layout-top-menu">
          {/* Left: Logo tab + title */}
          <div className="rmit-top-nav-left">
            <div className="rmit-logo-tab">
              <img
                src={this.props.assets.images.logoImage}
                alt="Logo"
                className="rmit-logo-img"
              />
            </div>
            <span className="rmit-top-nav-title">{branding.main.title}</span>
            {branding.main.shortTitle && (
              <span className="rmit-top-nav-short-title">{branding.main.shortTitle}</span>
            )}
          </div>

          {/* Right: Utility links (desktop) */}
          <div className="rmit-top-nav-right">
            {isAdmin && (
              <Popup
                trigger={
                  <span className="rmit-top-nav-link rmit-top-nav-toggle">
                    <Icon name="eye" style={{ margin: 0 }} />
                    <Checkbox
                      toggle
                      checked={researcherView}
                      onChange={() => this.props.userStore.toggleResearcherView()}
                    />
                  </span>
                }
                content="Toggle Researcher View"
                size="small"
                position="bottom center"
                inverted
              />
            )}
            {process.env.REACT_APP_ENABLE_GUIDE_DOWNLOAD === 'true' && (
              <a
                href="/static/files/race_guide.pdf"
                download="RACEHub User Guide.pdf"
                className="rmit-top-nav-link"
              >
                <Icon name="book" style={{ margin: 0 }} /> User Guide
              </a>
            )}
            <div className="rmit-user-menu-wrapper">
              <button
                className="rmit-top-nav-link rmit-user-menu-trigger"
                onClick={this.toggleUserMenu}
                aria-haspopup="true"
                aria-expanded={this.userMenuOpen}
              >
                <Icon name="user" style={{ margin: 0 }} /> <span className="rmit-user-menu-name">{displayName}</span>
                <Icon name={this.userMenuOpen ? 'chevron up' : 'chevron down'} style={{ margin: 0, fontSize: '0.7em', opacity: 0.6 }} />
              </button>
              {this.userMenuOpen && (
                <>
                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
                  <div className="rmit-user-menu-backdrop" onClick={this.closeUserMenu} />
                  <div className="rmit-user-menu-dropdown">
                    {isAdmin && !researcherView && (() => {
                      const times = this.getSessionTimes();
                      if (!times) return null;
                      return (
                        <div className="rmit-user-menu-session">
                          <div><strong>Session:</strong> {times.start}</div>
                          <div><strong>Expiry:</strong> {times.expiry}</div>
                        </div>
                      );
                    })()}
                    <button className="rmit-user-menu-item" onClick={this.handleRestoreGuides}>
                      <Icon name="redo" style={{ margin: 0 }} /> Restore Helper Messages
                    </button>
                    <div className="rmit-user-menu-divider" />
                    <button className="rmit-user-menu-item rmit-user-menu-signout" onClick={this.handleLogout}>
                      <Icon name="sign-out" style={{ margin: 0 }} /> Sign Out
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Hamburger button (mobile only) */}
          <button
            className="rmit-mobile-menu-btn"
            onClick={this.toggleMobileMenu}
            aria-label={this.mobileMenuOpen ? 'Close menu' : 'Open menu'}
          >
            <Icon name={this.mobileMenuOpen ? 'close' : 'bars'} style={{ margin: 0 }} />
          </button>
        </nav>

        {/* Fullscreen mobile menu overlay */}
        {this.mobileMenuOpen && this.renderMobileMenu(displayName, isAdmin, researcherView)}
      </>
    );
  }

  renderMobileMenu(displayName, isAdmin, researcherView) {
    const items = this.props.menuItems || [];
    const pathname = _.get(this.props.location, 'pathname', '');
    const is = value => _.startsWith(pathname, value);

    return (
      <div className="rmit-mobile-overlay">
        <div className="rmit-mobile-nav-items">
          {_.map(items, (item, index) => {
            const show = (_.isFunction(item.shouldShow) && item.shouldShow()) || item.shouldShow;
            if (!show) return null;
            if (researcherView && item.adminOnly) return null;
            if (item.body) return null;
            // Non-admin items are always visible in the sub-nav bar, skip them here
            if (!item.adminOnly) return null;

            // Flatten dropdown children into the mobile menu
            if (item.children && item.children.length > 0) {
              return _.map(item.children, (child, childIndex) => {
                const childShow = (_.isFunction(child.shouldShow) && child.shouldShow()) || child.shouldShow;
                if (!childShow) return null;
                if (researcherView && child.adminOnly) return null;
                const childActive = is(child.url);
                return (
                  <button
                    key={`${index}-${childIndex}`}
                    className={`rmit-mobile-nav-item${childActive ? ' active' : ''}`}
                    onClick={() => this.gotoMobile(child.url)}
                  >
                    {child.title}
                    <Icon name="chevron down" style={{ margin: 0, fontSize: '0.7em', opacity: 0.4 }} />
                  </button>
                );
              });
            }

            const active = is(item.url);
            return (
              <button
                key={index}
                className={`rmit-mobile-nav-item${active ? ' active' : ''}`}
                onClick={() => this.gotoMobile(item.url)}
              >
                {item.title}
                <Icon name="chevron down" style={{ margin: 0, fontSize: '0.7em', opacity: 0.4 }} />
              </button>
            );
          })}
        </div>

        <div className="rmit-mobile-utility">
          {isAdmin && (
            <div className="rmit-mobile-utility-item">
              <span><Icon name="eye" style={{ margin: 0 }} /> Researcher View</span>
              <Checkbox
                toggle
                checked={researcherView}
                onChange={() => this.props.userStore.toggleResearcherView()}
              />
            </div>
          )}
          {process.env.REACT_APP_ENABLE_GUIDE_DOWNLOAD === 'true' && (
            <a
              href="/static/files/race_guide.pdf"
              download="RACEHub User Guide.pdf"
              className="rmit-mobile-utility-item"
            >
              <Icon name="book" style={{ margin: 0 }} /> User Guide
            </a>
          )}
          <div className="rmit-mobile-utility-item rmit-mobile-user">
            <span>
              <Icon name="user" style={{ margin: 0 }} /> {displayName}
            </span>
          </div>
          <button
            className="rmit-mobile-utility-item"
            onClick={this.handleRestoreGuides}
          >
            <Icon name="redo" style={{ margin: 0 }} /> Restore Helper Messages
          </button>
          <button
            className="rmit-mobile-utility-item rmit-mobile-signout"
            onClick={this.handleLogout}
          >
            <Icon name="sign-out" style={{ margin: 0 }} /> Sign Out
          </button>
        </div>
      </div>
    );
  }
}

decorate(TopNav, {
  handleLogout: action,
  mobileMenuOpen: observable,
  userMenuOpen: observable,
  toggleMobileMenu: action,
  closeMobileMenu: action,
  toggleUserMenu: action,
  closeUserMenu: action,
  handleRestoreGuides: action,
});

export default inject('authentication', 'userStore', 'assets')(withRouter(observer(TopNav)));
